import React, { Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
} from 'react-router-dom';
import { useAppChromeContext } from '../contexts/AppChromeContext';
import { useAuthContext } from '../contexts/AuthContext';
import SplashScreen from '../components/common/SplashScreen';
import RouteError from '../components/common/RouteError';
import ActivityUnavailable from '../pages/ActivityDetail/ActivityUnavailable';
import featureToggles from '../config/featureToggles';
import lazyWithRetry from '../utils/lazyWithRetry';
import { BudgetProvider } from '../contexts/BudgetContext';
import { TreasuryProvider } from '../contexts/TreasuryContext';

const enableExplorePage = import.meta.env.VITE_ENABLE_EXPLORE_PAGE === 'true';

const HomePage = lazyWithRetry(() => import('../pages/Home'));
const WelcomePage = lazyWithRetry(() => import('../pages/Welcome'));
const FamilyFormsPage = lazyWithRetry(() => import('../pages/FamilyForms'));
const ContributionsPage = lazyWithRetry(() => import('../pages/Finance/ContributionsPage'));
const ExpenseRequestsPage = lazyWithRetry(() => import('../pages/Finance/ExpenseRequestsPage'));
const FinancePage = lazyWithRetry(() => import('../pages/Finance'));
const StatisticsPage = lazyWithRetry(() => import('../pages/Statistics'));
const ProgressPage = lazyWithRetry(() => import('../pages/Progress'));
const ActivitiesDashboardPage = lazyWithRetry(() => import('../pages/ActivitiesDashboard/ActivitiesDashboardPage'));
const ExplorePage = lazyWithRetry(() => import('../pages/Explore'));
const CalendarPage = lazyWithRetry(() => import('../pages/Calendar'));
const CampaignPage = lazyWithRetry(() => import('../pages/Campaign'));
const DiscoverPage = lazyWithRetry(() => import('../pages/Discover/DiscoverPage'));
const CreatePage = lazyWithRetry(() => import('../pages/Create'));
const EventCreatePage = lazyWithRetry(() => import('../pages/EventCreate'));
const NewsPage = lazyWithRetry(() => import('../pages/News/NewsPage.jsx'));
const NotificationsPage = lazyWithRetry(() => import('../pages/Notifications/NotificationsPage'));
const ChatPage = lazyWithRetry(() => import('../pages/Chat/ChatPage'));
const ActivityDetailPage = lazyWithRetry(() => import('../pages/ActivityDetail/ActivityDetailPage'));
const EventDetailPage = lazyWithRetry(() => import('../pages/EventDetail/EventDetailPage'));
const HouseholdDetailPage = lazyWithRetry(() => import('../pages/HouseholdDetail'));
const HouseholdPage = lazyWithRetry(() => import('../pages/Household'));
const SearchPage = lazyWithRetry(() => import('../pages/Search'));
const DonatePage = lazyWithRetry(() => import('../pages/Donate'));

const StudyCircles = lazyWithRetry(() => import('../pages/StudyCircles'));
const JuniorYouthProgram = lazyWithRetry(() => import('../pages/JuniorYouthProgram'));
const ChildrensClasses = lazyWithRetry(() => import('../pages/ChildrensClasses'));
const Devotionals = lazyWithRetry(() => import('../pages/Devotionals'));

const NewActivityForm = lazyWithRetry(() => import('../components/forms/NewActivityForm'));
const Dashboard = lazyWithRetry(() => import('../components/experimental/Dashboard'));
const BudgetOverview = lazyWithRetry(() => import('../components/experimental/BudgetOverview'));
const Profile = lazyWithRetry(() => import('../pages/Profile'));
const PublicProfilePage = lazyWithRetry(() => import('../pages/PublicProfile'));
const PostPage = lazyWithRetry(() => import('../pages/PostDetail/PostDetailPage'));
const ReceiptSubmission = lazyWithRetry(() => import('../components/experimental/ReceiptSubmission'));
const LoginPage = lazyWithRetry(() => import('../pages/Login'));
const ForgotPassword = lazyWithRetry(() => import('../pages/ForgotPassword'));
const ForgotBahaiId = lazyWithRetry(() => import('../pages/ForgotBahaiId'));
const ResetPasswordPage = lazyWithRetry(() => import('../pages/ResetPasswordToken'));
const BahaiIdVerificationPage = lazyWithRetry(() => import('../pages/BahaiIdVerification'));
const ActivityConfirm = lazyWithRetry(() => import('../pages/ActivityConfirm'));
const SessionConfirm = lazyWithRetry(() => import('../pages/SessionConfirm'));
const LayoutWithNavbar = lazyWithRetry(() => import('../components/layout/LayoutWithNavbar'));
const AnimatedOutlet = lazyWithRetry(() => import('../components/layout/AnimatedOutlet'));
const RequireAuth = lazyWithRetry(() => import('../components/auth/RequireAuth'));
const RequireAssembly = lazyWithRetry(() => import('../components/auth/RequireAssembly'));
const RequireMaintainer = lazyWithRetry(() => import('../components/auth/RequireMaintainer'));
const SettingsPage = lazyWithRetry(() => import('../pages/Settings'));
const MaintainerLayout = lazyWithRetry(() => import('../components/maintainer/MaintainerLayout'));

const PrivacyPolicyPage = lazyWithRetry(() => import('../pages/PrivacyPolicy'));
const AppleCallbackPage = lazyWithRetry(() => import('../pages/AppleCallback/AppleCallbackPage'));
const FaqsPage = lazyWithRetry(() => import('../pages/Faqs'));
const AvailableAreasPage = lazyWithRetry(() => import('../pages/AvailableAreas'));
const CommunityStandardsPage = lazyWithRetry(() => import('../pages/CommunityStandards'));
const ChildProtectionCertificationPage = lazyWithRetry(() => import('../pages/ChildProtectionCertificationPage'));
const ClusterDetailPage = lazyWithRetry(() => import('../pages/CommunityDetail/ClusterDetailPage'));
const CommunityDetailPage = lazyWithRetry(() => import('../pages/CommunityDetail'));
const EulaPage = lazyWithRetry(() => import('../pages/Eula'));
const NuriTestPage = lazyWithRetry(() => import('../pages/NuriTest'));
const NuriAdminPage = lazyWithRetry(() => import('../pages/NuriAdmin'));
const NuriSupportPage = lazyWithRetry(() => import('../pages/NuriSupport'));
const NuriDeleteAccountPage = lazyWithRetry(() => import('../pages/NuriDeleteAccount'));
const TestBasePage = lazyWithRetry(() => import('../pages/TestBasePage'));
const BaseDetailPage = lazyWithRetry(() => import('../pages/BaseDetailPage'));
const NotAuthorizedPage = lazyWithRetry(() => import('../pages/NotAuthorized'));
const MaintainerOnboardingPage = lazyWithRetry(() => import('../pages/MaintainerOnboarding'));
const MaintainerDashboardPage = lazyWithRetry(() => import('../pages/Maintainer/MaintainerDashboardPage'));
const MaintainerCommunitiesPage = lazyWithRetry(() => import('../pages/Maintainer/MaintainerCommunitiesPage'));
const MaintainerMembersPage = lazyWithRetry(() => import('../pages/Maintainer/MaintainerMembersPage'));
const MaintainerBodiesPage = lazyWithRetry(() => import('../pages/Maintainer/MaintainerBodiesPage'));
const MaintainerRolesPage = lazyWithRetry(() => import('../pages/Maintainer/MaintainerRolesPage'));
const MaintainerCommunityDetailPage = lazyWithRetry(() => import('../pages/Maintainer/MaintainerCommunityDetailPage'));
const MaintainerImportsPage = lazyWithRetry(() => import('../pages/Maintainer/MaintainerImportsPage'));
const MaintainerMetricsPage = lazyWithRetry(() => import('../pages/Maintainer/MaintainerMetricsPage'));
const MaintainerPermissionsPage = lazyWithRetry(() => import('../pages/MaintainerAdmin/MaintainerPermissionsPage'));

const withFinanceData = (element) => (
  <TreasuryProvider>
    <BudgetProvider>
      {element}
    </BudgetProvider>
  </TreasuryProvider>
);


const WelcomeRoute = () => {
  const { onSignIn } = useAppChromeContext();
  return (
    <Suspense fallback={<SplashScreen />}>
      <WelcomePage onSignIn={onSignIn} />
    </Suspense>
  );
};

const LoginRoute = () => {
  const { onSignIn } = useAppChromeContext();
  return <LoginPage onSignIn={onSignIn} />;
};

const DiscoverRoute = () => {
  const { discoverPanel } = useAppChromeContext();
  return <DiscoverPage selectedPanel={discoverPanel} />;
};

const CampaignRoute = () => {
  const { administrationPanel } = useAppChromeContext();
  return <CampaignPage selectedPanel={administrationPanel} />;
};

const ProfileRoute = () => {
  const { user } = useAuthContext();
  return <Profile user={user} />;
};

const SettingsRoute = () => {
  const { user } = useAuthContext();
  return <SettingsPage user={user} />;
};

const AuthenticatedLayoutRoute = () => {
  const {
    showNotificationsPanel,
    onToggleNotifications,
    onCloseNotifications,
    setDiscoverPanel,
    setAdministrationPanel,
  } = useAppChromeContext();

  return (
    <LayoutWithNavbar
      onToggleNotifications={onToggleNotifications}
      onNotificationClick={onCloseNotifications}
      showNotificationsPanel={showNotificationsPanel}
      setSelectedDiscoverPanel={setDiscoverPanel}
      setSelectedAdminPanel={setAdministrationPanel}
    >
      <Suspense fallback={<SplashScreen />}>
        <AnimatedOutlet />
      </Suspense>
    </LayoutWithNavbar>
  );
};

export const createAppRouter = ({
  isAuthenticated,
  user,
  token,
}) => {
  const authenticated = typeof isAuthenticated === 'boolean'
    ? isAuthenticated
    : Boolean(user && token);

  const sharedPublicChildren = [
    { path: 'community/:name', element: <CommunityDetailPage /> },
    { path: 'clusters/:clusterId', element: <ClusterDetailPage /> },
    { path: 'login', element: (<LoginRoute />) },
    { path: 'sign-in', element: (<LoginRoute />) },
    { path: 'forgot-password', element: (<ForgotPassword />) },
    { path: 'reset-password/:token', element: (<ResetPasswordPage />) },
    { path: 'forgot-bahai-id', element: (<ForgotBahaiId />) },
    { path: 'verify-bahai-id', element: (<BahaiIdVerificationPage />) },
    { path: 'nuri/test', element: (<NuriTestPage />) },
    { path: 'nuri/delete-my-account', element: (<NuriDeleteAccountPage />) },
    { path: 'nuri-support', element: (<NuriSupportPage />) },
    { path: 'privacy-policy', element: (<PrivacyPolicyPage />) },
    { path: 'apple/callback', element: (<AppleCallbackPage />) },
    { path: 'eula', element: (<EulaPage />) },
    { path: 'community-standards', element: (<CommunityStandardsPage />) },
    { path: 'faqs', element: (<FaqsPage />) },
    { path: 'available-communities', element: (<AvailableAreasPage />) },
    ...(enableExplorePage ? [{ path: 'explore', element: (<ExplorePage />) }] : []),
  ];

  // Informational public pages (footer links) keep the top nav with the
  // public brand + Log in / Sign up controls by rendering inside the shared
  // layout; auth flows (login, password resets, ...) stay full-screen.
  const publicChromePaths = new Set([
    'community/:name',
    'clusters/:clusterId',
    'privacy-policy',
    'eula',
    'community-standards',
    'faqs',
    'available-communities',
    ...(enableExplorePage ? ['explore'] : []),
  ]);

  const publicRoutes = [
    {
      path: '/',
      element: <WelcomeRoute />,
      errorElement: <RouteError />,
    },
    ...sharedPublicChildren
      .filter((route) => !publicChromePaths.has(route.path))
      .map((route) => ({
        ...route,
        path: `/${route.path}`,
        errorElement: <RouteError />,
      })),
    {
      path: '/',
      element: <AuthenticatedLayoutRoute />,
      errorElement: <RouteError />,
      children: sharedPublicChildren.filter((route) => publicChromePaths.has(route.path)),
    },
    // Shared and protected links must reach the welcome screen when signed out.
    { path: '*', element: <Navigate to="/" replace /> },
  ];

  const authenticatedChildren = [
    { index: true, element: (<RequireAuth>{withFinanceData(<HomePage />)}</RequireAuth>) },
    ...sharedPublicChildren,
    { path: 'dashboard-experimental', element: (<RequireAuth><Dashboard /></RequireAuth>) },
    { path: 'create', element: (<RequireAuth><CreatePage /></RequireAuth>) },
    { path: 'event/new', element: (<RequireAuth><EventCreatePage /></RequireAuth>) },
    { path: 'dashboard', element: (<RequireAuth><ActivitiesDashboardPage /></RequireAuth>) },
    { path: 'activities', element: (<RequireAuth><ActivitiesDashboardPage /></RequireAuth>) },
    { path: 'activities/:activityId', element: (<ActivityDetailPage />), errorElement: (<ActivityUnavailable />) },
    { path: 'activities/:activityId/confirm', element: (<ActivityConfirm />) },
    { path: 'sessions/:sessionId/confirm', element: (<SessionConfirm />) },
    { path: 'events/:eventId', element: (
      <RequireAuth>
        <Suspense fallback={<div>Loading Event Details...</div>}>
          <EventDetailPage />
        </Suspense>
      </RequireAuth>
    ) },
    { path: 'budget', element: (<RequireAuth><BudgetOverview /></RequireAuth>) },
    { path: 'discover', element: (<RequireAuth><DiscoverRoute /></RequireAuth>) },
    { path: 'news', element: (<RequireAuth><NewsPage /></RequireAuth>) },
    { path: 'forms', element: (<RequireAuth><FamilyFormsPage /></RequireAuth>) },
    { path: 'forms/:requestId', element: (<RequireAuth><FamilyFormsPage /></RequireAuth>) },
    { path: 'notifications', element: (<RequireAuth><NotificationsPage /></RequireAuth>) },
    { path: 'chat', element: (<RequireAuth><ChatPage /></RequireAuth>) },
    { path: 'search', element: (<RequireAuth><SearchPage /></RequireAuth>) },
    { path: 'statistics', element: (<RequireAuth><StatisticsPage /></RequireAuth>) },
    { path: 'progress', element: (<RequireAuth><ProgressPage /></RequireAuth>) },
    { path: 'submit-receipt', element: (<RequireAuth><ReceiptSubmission /></RequireAuth>) },
    { path: 'my-account', element: (<RequireAuth><ProfileRoute /></RequireAuth>) },
    { path: 'household', element: (<RequireAuth><HouseholdPage /></RequireAuth>) },
    { path: 'household/:householdId', element: (<RequireAuth><HouseholdDetailPage /></RequireAuth>) },
    { path: 'users/:userId', element: (<PublicProfilePage />) },
    { path: 'og/posts/:postId', element: (<PostPage />) },
    { path: 'posts/:postId', element: (<PostPage />) },
    { path: 'not-authorized', element: (<RequireAuth><NotAuthorizedPage /></RequireAuth>) },
    {
      path: 'maintainer',
      element: (
        <RequireAuth>
          <RequireMaintainer>
            <MaintainerLayout />
          </RequireMaintainer>
        </RequireAuth>
      ),
      children: [
        { index: true, element: <MaintainerDashboardPage /> },
        { path: 'communities', element: <MaintainerCommunitiesPage /> },
        { path: 'communities/:communityId', element: <MaintainerCommunityDetailPage /> },
        { path: 'members', element: <MaintainerMembersPage /> },
        { path: 'bodies', element: <MaintainerBodiesPage /> },
        { path: 'roles', element: <MaintainerRolesPage /> },
        { path: 'imports', element: <MaintainerImportsPage /> },
        { path: 'metrics', element: <MaintainerMetricsPage /> },
        { path: 'onboard', element: <MaintainerOnboardingPage /> },
        { path: 'permissions', element: <MaintainerPermissionsPage /> },
      ]
    },
    { path: 'campaign', element: (<RequireAuth><CampaignRoute /></RequireAuth>) },
    { path: 'calendars', element: (<RequireAuth><CalendarPage /></RequireAuth>) },
    { path: 'finance/contributions', element: (<RequireAuth><ContributionsPage /></RequireAuth>) },
    { path: 'finance/expenses', element: (<RequireAuth>{withFinanceData(<ExpenseRequestsPage />)}</RequireAuth>) },
    { path: 'finance', element: (<RequireAuth>{withFinanceData(<FinancePage />)}</RequireAuth>) },
    { path: 'treasury', element: <Navigate to="/finance" replace /> },
    { path: 'nuri-admin', element: (
      <RequireAuth>
        <RequireAssembly>
          <NuriAdminPage />
        </RequireAssembly>
      </RequireAuth>
    ) },
    { path: 'settings', element: (<RequireAuth><SettingsRoute /></RequireAuth>) },
    { path: 'study-circles', element: (<RequireAuth><StudyCircles /></RequireAuth>) },
    { path: 'junior-youth-program', element: (<RequireAuth><JuniorYouthProgram /></RequireAuth>) },
    { path: 'childrens-classes', element: (<RequireAuth><ChildrensClasses /></RequireAuth>) },
    { path: 'devotionals', element: (<RequireAuth><Devotionals /></RequireAuth>) },
    { path: 'study-circles/new', element: (<RequireAuth><NewActivityForm activityType="Study Circle" /></RequireAuth>) },
    { path: 'junior-youth-program/new', element: (<RequireAuth><NewActivityForm activityType="Junior Youth Group" /></RequireAuth>) },
    { path: 'childrens-classes/new', element: (<RequireAuth><NewActivityForm activityType={'Children\'s Class'} /></RequireAuth>) },
    { path: 'devotionals/new', element: (<RequireAuth><NewActivityForm activityType="Devotional" /></RequireAuth>) },
    { path: 'fireside/new', element: (<RequireAuth><NewActivityForm activityType="Fireside" /></RequireAuth>) },
    { path: 'initiative/new', element: (<RequireAuth><NewActivityForm activityType="Independent Initiative" /></RequireAuth>) },
    { path: 'test/base', element: (<RequireAuth><TestBasePage /></RequireAuth>) },
    { path: 'test/detail', element: (<RequireAuth><BaseDetailPage /></RequireAuth>) },
    ...(featureToggles.showCryptoTreasury
      ? [{ path: 'donate', element: withFinanceData(<RequireAuth><DonatePage /></RequireAuth>) }]
      : []),
    { path: 'child-protection-certification', element: (<RequireAuth><ChildProtectionCertificationPage /></RequireAuth>) },
  ];

  const routes = authenticated
    ? [{
        path: '/',
        element: <AuthenticatedLayoutRoute />,
        errorElement: <RouteError />,
        children: authenticatedChildren,
      }]
    : publicRoutes;

  return createBrowserRouter(routes);
};
